
import React from 'react';
import { format, subDays } from 'date-fns';
import { Calendar } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { InventoryItem } from '@/types/inventory';
import { cn } from '@/lib/utils';
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ReportSettingsProps {
  dateRange: '7' | '30' | 'custom';
  setDateRange: (range: '7' | '30' | 'custom') => void;
  fromDate: Date | undefined;
  setFromDate: (date: Date | undefined) => void;
  toDate: Date | undefined;
  setToDate: (date: Date | undefined) => void;
  filterMode: 'all' | 'selected';
  setFilterMode: (mode: 'all' | 'selected') => void;
  inventoryItems: InventoryItem[];
  selectedItems: Record<string, boolean>;
  toggleAllItems: (checked: boolean) => void;
  toggleItem: (id: string, checked: boolean) => void;
  onPreview: () => void;
}

const ReportSettings: React.FC<ReportSettingsProps> = ({
  dateRange,
  setDateRange,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  filterMode,
  setFilterMode,
  inventoryItems,
  selectedItems,
  toggleAllItems,
  toggleItem,
  onPreview
}) => {
  return (
    <div className="grid gap-4 py-4">
      <div className="space-y-4">
        <h3 className="font-medium">Select Report Period</h3>
        
        <RadioGroup 
          value={dateRange} 
          onValueChange={(value) => setDateRange(value as '7' | '30' | 'custom')}
          className="grid grid-cols-3 gap-4"
        >
          <div>
            <RadioGroupItem 
              value="7" 
              id="last7" 
              className="peer sr-only" 
            />
            <Label
              htmlFor="last7"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-white p-4 hover:bg-slate-50 hover:border-slate-200 peer-data-[state=checked]:border-emerald-600 [&:has([data-state=checked])]:border-emerald-600"
            >
              <span>Last 7 Days</span>
            </Label>
          </div>
          
          <div>
            <RadioGroupItem 
              value="30" 
              id="last30" 
              className="peer sr-only" 
            />
            <Label
              htmlFor="last30"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-white p-4 hover:bg-slate-50 hover:border-slate-200 peer-data-[state=checked]:border-emerald-600 [&:has([data-state=checked])]:border-emerald-600"
            >
              <span>Last 30 Days</span>
            </Label>
          </div>
          
          <div>
            <RadioGroupItem 
              value="custom" 
              id="custom" 
              className="peer sr-only" 
            />
            <Label
              htmlFor="custom"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-white p-4 hover:bg-slate-50 hover:border-slate-200 peer-data-[state=checked]:border-emerald-600 [&:has([data-state=checked])]:border-emerald-600"
            >
              <span>Custom Range</span>
            </Label>
          </div>
        </RadioGroup>
        
        {dateRange === 'custom' && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="from">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="from"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !fromDate && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {fromDate ? format(fromDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={fromDate}
                    onSelect={setFromDate}
                    initialFocus
                    disabled={(date) => date > (toDate || new Date())}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="to">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="to"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !toDate && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {toDate ? format(toDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={toDate}
                    onSelect={setToDate}
                    initialFocus
                    disabled={(date) => fromDate ? date < fromDate : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
      </div>
      
      <div className="space-y-4 mt-2">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Filter Items to Include</h3>
          <RadioGroup 
            value={filterMode} 
            onValueChange={(value) => setFilterMode(value as 'all' | 'selected')}
            className="flex gap-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="filter-all" />
              <Label htmlFor="filter-all">All Items</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="selected" id="filter-selected" />
              <Label htmlFor="filter-selected">Selected Items</Label>
            </div>
          </RadioGroup>
        </div>
        
        {filterMode === 'selected' && (
          <div className="border rounded-md max-h-[200px] overflow-y-auto p-2">
            <div className="sticky top-0 bg-white pb-2 border-b mb-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={Object.values(selectedItems).every(Boolean) && Object.keys(selectedItems).length > 0}
                  onCheckedChange={(checked) => toggleAllItems(!!checked)}
                />
                <Label htmlFor="select-all" className="font-medium">Select All Items</Label>
              </div>
            </div>
            
            <div className="space-y-2">
              {inventoryItems.map(item => (
                <div key={item.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`item-${item.id}`}
                    checked={selectedItems[item.id] || false}
                    onCheckedChange={(checked) => toggleItem(item.id, !!checked)}
                  />
                  <Label htmlFor={`item-${item.id}`} className="text-sm">
                    {item.name} 
                    <span className="text-xs text-gray-500 ml-2">
                      ({item.category}
                      {item.has_expiry && item.expiry_date 
                        ? `, expires ${format(new Date(item.expiry_date), 'MMM d, yyyy')}` 
                        : ''}
                      )
                    </span>
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-4 space-y-2">
        <h3 className="font-medium">Report will include:</h3>
        <ul className="text-sm space-y-1 text-gray-600 pl-5">
          <li>• Summary statistics of your inventory</li>
          <li>• Visual charts with enhanced styling: pie, bar, and line charts</li>
          <li>• Item list filtered according to your preferences</li>
          <li>• AI-generated analysis of your inventory health</li>
        </ul>
      </div>
      
      <div className="flex justify-end mt-4">
        <Button 
          onClick={onPreview} 
          variant="outline"
          className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
        >
          Preview Report
        </Button>
      </div>
    </div>
  );
};

export default ReportSettings;
